import React, { forwardRef } from 'react';
import { InfinityMark } from './ui';
import { fmtXp, iconFor } from '../utils/gamification';
import { initials, longDate, medalSentence } from '../utils/share';
import './ShareCard.css';

// Cores fixas: a imagem sai igual nos temas claro e escuro. O círculo usa o fundo e o ícone do
// tier no tema claro; o anel, o selo e os raios usam a cor do tier no escuro, que contrasta com o navy.
const TIER = {
  gold: { label: 'ouro', bg: '#fbf0d6', fg: '#c98a00', ring: '#f2b632', rgb: '242, 182, 50' },
  silver: { label: 'prata', bg: '#e9ecf5', fg: '#7c86a6', ring: '#b6bfdb', rgb: '182, 191, 219' },
  bronze: { label: 'bronze', bg: '#f5e6da', fg: '#b0703f', ring: '#e0a274', rgb: '224, 162, 116' },
};

// Preenchimento e traço do anel vão como atributos: o html-to-image não leva o CSS do SVG para o PNG
const R = 50;
const C = 2 * Math.PI * R;

/** Imagem 1200×627 (post do LinkedIn): progresso no curso, nível alcançado ou medalha. */
const ShareCard = forwardRef(function ShareCard({ kind, courseName, userName, showName, progress, level, medal }, ref) {
  return (
    <div className="shc" ref={ref}>
      <span className="shc__strip" aria-hidden="true" />
      <InfinityMark className="shc__mark" strokeWidth={4} />

      <div className="shc__top">
        <span className="shc__brand"><span>&lt;</span>StudySpace<span>/&gt;</span></span>
        {courseName && <span className="shc__course">{courseName}</span>}
      </div>

      {kind === 'progress' && progress && <ProgressBody progress={progress} />}
      {kind === 'level' && level && <LevelBody level={level} courseName={courseName} />}
      {kind === 'medal' && medal && <MedalBody medal={medal} />}

      <div className="shc__foot">
        {showName && userName ? (
          <span className="shc__who">
            <span className="shc__avatar">{initials(userName)}</span>
            <strong>{userName}</strong>
          </span>
        ) : (
          <span>Estudando no StudySpace</span>
        )}
        <span>{longDate()}</span>
      </div>
    </div>
  );
});

function ProgressBody({ progress: p }) {
  return (
    <div className="shc__main">
      <div className="shc__ring">
        <svg viewBox="0 0 120 120" width="300" height="300" aria-hidden="true">
          <circle cx="60" cy="60" r={R} fill="none" stroke="rgba(255, 255, 255, 0.12)" strokeWidth="9" />
          <circle
            cx="60"
            cy="60"
            r={R}
            fill="none"
            stroke="#a4dc00"
            strokeWidth="9"
            strokeLinecap="round"
            strokeDasharray={`${(p.pct / 100) * C} ${C}`}
            transform="rotate(-90 60 60)"
          />
        </svg>
        <span className="shc__ringtext">
          <span className="shc__ringlabel">Nível</span>
          <span className="shc__ringnum">{p.level}</span>
        </span>
      </div>
      <div className="shc__side">
        <h3 className="shc__title">Meu progresso no curso</h3>
        <div className="shc__stats">
          <div><strong>{fmtXp(p.xp)}</strong><span>XP no curso</span></div>
          <div><strong>{p.medals} <small>de {p.medalsTotal}</small></strong><span>medalhas</span></div>
          <div>
            <strong><em>{p.tiers.gold}</em> · {p.tiers.silver} · {p.tiers.bronze}</strong>
            <span>ouro · prata · bronze</span>
          </div>
          <div>
            <strong>{p.streak} {p.streak === 1 ? 'dia' : 'dias'}</strong>
            <span>{p.streak === 1 ? 'de estudo seguido' : 'seguidos de estudo'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function LevelBody({ level, courseName }) {
  return (
    <div className="shc__main">
      <div className="shc__ring">
        <svg viewBox="0 0 120 120" width="300" height="300" aria-hidden="true">
          <circle cx="60" cy="60" r={R} fill="none" stroke="#a4dc00" strokeWidth="9" />
        </svg>
        <span className="shc__ringtext">
          <span className="shc__ringnum shc__ringnum--big">{level.level}</span>
        </span>
      </div>
      <div className="shc__side shc__side--tight">
        <span className="shc__eyebrow">Subi de nível</span>
        <h3 className="shc__title shc__title--xl">Nível {level.level} alcançado</h3>
        <p className="shc__text">
          {fmtXp(level.xp)} XP em aulas, simulados e revisões{courseName ? ` de ${courseName}` : ''}.
        </p>
      </div>
    </div>
  );
}

function MedalBody({ medal }) {
  const tier = TIER[medal.tier] || TIER.bronze;
  const Icon = iconFor(medal.icon);
  return (
    <div className="shc__main">
      <div className="shc__medal">
        <span
          className="shc__rays"
          style={{ background: `repeating-conic-gradient(rgba(${tier.rgb}, 0.16) 0deg 12deg, transparent 12deg 24deg)` }}
          aria-hidden="true"
        />
        <span
          className="shc__seal"
          style={{ background: tier.bg, color: tier.fg, boxShadow: `0 0 0 10px #1c2d63, 0 0 0 16px ${tier.ring}` }}
        >
          <Icon size={110} strokeWidth={1.6} aria-hidden="true" />
        </span>
      </div>
      <div className="shc__side shc__side--tight shc__side--start">
        <span className="shc__badge" style={{ background: `rgba(${tier.rgb}, 0.18)`, color: tier.ring }}>
          Medalha {tier.label}{medal.rarity ? ` · ${medal.rarity}` : ''}
        </span>
        <h3 className="shc__title shc__title--xl">{medal.name}</h3>
        <p className="shc__text">{medalSentence(medal)}</p>
      </div>
    </div>
  );
}

export default ShareCard;
